import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Plus, Building } from 'lucide-react';
import { getOrganizations } from '@/lib/firebase/queries/organizations';
import { AddOrganizationDialog } from './AddOrganizationDialog';
import type { Organization } from '@/types/organization';
import { ManageOrganizationDialog } from './ManageOrganizationDialog';
import { DeleteOrganizationDialog } from './DeleteOrganizationDialog';

import { OrganizationActions } from './OrganizationActions';
import { useLanguage } from '@/contexts/LanguageContext';

export function OrganizationList() {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [orgToDelete, setOrgToDelete] = useState<Organization | null>(null);

  const { t, dir } = useLanguage();

  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      const orgs = await getOrganizations();
      setOrganizations(orgs);
    } catch (error) {
      console.error('Error fetching organizations:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrganizations();
  }, []);

  return (
    <div className="space-y-4" dir={dir}>
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">{t('dashboard.admin.organizations.title')}</h2>
        <Button onClick={() => setShowAddDialog(true)}>
          <Plus className={`h-4 w-4 ${dir === 'rtl' ? 'ml-2' : 'mr-2'}`} />
          {t('dashboard.admin.organizations.addOrganization')}
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {organizations.map((org) => (
          <Card 
            key={org.id}
            className="p-6 hover:bg-muted/50 transition-colors"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4 flex-1">
                {/* Organization Logo */}
                {org.logoUrl ? (
                  <div className="flex-shrink-0">
                    <img 
                      src={org.logoUrl} 
                      alt={org.name}
                      className="h-12 w-auto max-w-[80px] object-contain rounded"
                    />
                  </div>
                ) : (
                  <div className="flex-shrink-0 h-12 w-12 bg-muted rounded flex items-center justify-center">
                    <Building className="h-6 w-6 text-muted-foreground" />
                  </div>
                )}
                
                {/* Organization Info */}
                <div className="space-y-1 min-w-0 flex-1">
                  <h3 className="font-medium">{org.name}</h3>
                  {org.nameEn && org.nameEn !== org.name && (
                    <p className="text-sm text-muted-foreground">
                      {org.nameEn}
                    </p>
                  )}
                  {org.description && (
                    <p className="text-sm text-muted-foreground">
                      {org.description}
                    </p>
                  )}
                </div>
              </div>
              
              <OrganizationActions 
                onManage={() => setSelectedOrg(org)}
                onDelete={() => setOrgToDelete(org)}
              />
            </div>
          </Card>
        ))}

        {organizations.length === 0 && !loading && (
          <div className="col-span-full flex flex-col items-center justify-center p-8 text-center">
            <Building className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="font-medium mb-2">{t('dashboard.admin.organizations.table.noOrganizations')}</h3>
            <p className="text-sm text-muted-foreground">
              {t('dashboard.admin.organizations.description')}
            </p>
          </div>
        )}
      </div>

      <AddOrganizationDialog
        open={showAddDialog}
        onOpenChange={setShowAddDialog}
        onSuccess={fetchOrganizations}
      />

      <ManageOrganizationDialog
        organization={selectedOrg}
        open={!!selectedOrg}
        onOpenChange={(open) => !open && setSelectedOrg(null)}
        onSuccess={fetchOrganizations}
      />

      <DeleteOrganizationDialog
        organization={orgToDelete}
        open={!!orgToDelete}
        onOpenChange={(open) => !open && setOrgToDelete(null)}
        onSuccess={fetchOrganizations}
      />
    </div>
  );
}